
import { useState, useCallback, useEffect } from "react";
import Papa, { ParseConfig, ParseResult } from "papaparse";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useCategories } from "@/hooks/useCategories";
import { 
  ColumnMapping, 
  ImportSettings, 
  ParsedQuote, 
  ImportResults,
  ImportError,
  CategoryResult
} from "@/types/import";

interface CSVRowData {
  [key: string]: string;
}

export const useCSVImport = () => {
  const { toast } = useToast();
  const { categories } = useCategories();
  
  // State for file and parsing
  const [file, setFile] = useState<File | null>(null);
  const [parsedData, setParsedData] = useState<ParseResult<CSVRowData>>();
  const [previewData, setPreviewData] = useState<ParsedQuote[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  
  // State for import process
  const [isImporting, setIsImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [importResults, setImportResults] = useState<ImportResults>();
  const [abortController, setAbortController] = useState<AbortController | null>(null);
  
  // Default settings - Changed createMissingCategories to false as requested
  const [columnMapping, setColumnMapping] = useState<ColumnMapping>({
    content: "",
    author: "",
    source: "",
    categories: "",
    categoryDelimiter: ","
  });
  
  const [importSettings, setImportSettings] = useState<ImportSettings>({
    delimiter: ",",
    encoding: "UTF-8",
    hasHeader: true,
    trimFields: true,
    skipEmptyLines: true,
    createMissingCategories: false, // Changed to false by default
    skipDuplicateQuotes: true,
    batchSize: 100,
    enableDeletion: false
  });

  // Parse CSV file when it changes or settings change
  useEffect(() => {
    if (!file) return;
    
    parseFile();
  }, [file, importSettings.delimiter, importSettings.encoding, importSettings.hasHeader]);

  // Process the data for preview when parsing is complete
  useEffect(() => {
    if (!parsedData || !parsedData.data) return;
    
    processDataForPreview();
  }, [parsedData, columnMapping, importSettings.enableDeletion]);

  // Auto-detect and map common columns
  useEffect(() => {
    if (!parsedData || !parsedData.meta?.fields) return;
    
    const fields = parsedData.meta.fields;
    const newMapping = { ...columnMapping };
    
    // Filter out empty header names
    const validFields = fields.filter(field => field && field.trim() !== '');
    
    if (validFields.length === 0) {
      setError("The CSV file contains empty or invalid column headers. Please check your file.");
      return;
    }
    
    // Auto-map common column names (case insensitive)
    validFields.forEach(field => {
      const fieldLower = field.toLowerCase();
      
      if (fieldLower.includes('content') || fieldLower.includes('quote') || fieldLower.includes('text')) {
        newMapping.content = field;
      }
      else if (fieldLower.includes('author') || fieldLower.includes('by')) {
        newMapping.author = field;
      }
      else if (fieldLower.includes('source') || fieldLower.includes('from')) {
        newMapping.source = field;
      }
      else if (fieldLower.includes('categor') || fieldLower.includes('tag')) {
        newMapping.categories = field;
      }
    });
    
    setColumnMapping(newMapping);
  }, [parsedData?.meta?.fields]);

  // Parse the CSV file
  const parseFile = useCallback(async () => {
    if (!file) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      // Read the file as text
      const text = await readFileAsText(file, importSettings.encoding);
      
      // Define parse config with correct typing
      const parseConfig: ParseConfig<CSVRowData> = {
        header: importSettings.hasHeader,
        delimiter: importSettings.delimiter,
        skipEmptyLines: importSettings.skipEmptyLines,
        transform: (value) => {
          // Handle empty values
          return value.trim();
        },
        complete: (results: ParseResult<CSVRowData>) => {
          // Check for empty headers
          if (results.meta && results.meta.fields) {
            const hasEmptyHeaders = results.meta.fields.some(field => !field || field.trim() === '');
            if (hasEmptyHeaders) {
              const validFields = results.meta.fields.filter(field => field && field.trim() !== '');
              results.meta.fields = validFields;
              
              toast({
                title: "Warning: Empty Headers Detected",
                description: "Some column headers in your CSV are empty and have been removed.",
                variant: "destructive",
              });
            }
          }
          
          setParsedData(results);
          setIsLoading(false);
        },
        error: (error) => {
          setError(`Error parsing CSV: ${error.message}`);
          setIsLoading(false);
        }
      };

      // Parse the CSV
      Papa.parse(text, parseConfig);
    } catch (err) {
      setError(`Error reading file: ${err instanceof Error ? err.message : String(err)}`);
      setIsLoading(false);
    }
  }, [file, importSettings, toast]);

  // Process the parsed data for preview
  const processDataForPreview = useCallback(() => {
    if (!parsedData || !parsedData.data || !columnMapping.content) return;
    
    try {
      const processedData: ParsedQuote[] = parsedData.data.map((row) => {
        const quote: ParsedQuote = {
          content: row[columnMapping.content]?.trim() || "",
          validationErrors: []
        };
        
        // Add author if mapped
        if (columnMapping.author && row[columnMapping.author]) {
          quote.author = row[columnMapping.author].trim();
        }
        
        // Add source if mapped
        if (columnMapping.source && row[columnMapping.source]) {
          quote.source = row[columnMapping.source].trim();
        }
        
        // Process categories if mapped
        if (columnMapping.categories && row[columnMapping.categories]) {
          const categoriesString = row[columnMapping.categories].trim();
          if (categoriesString) {
            const categories = categoriesString
              .split(columnMapping.categoryDelimiter || ",")
              .map(cat => cat.trim())
              .filter(cat => cat.length > 0);
            
            // Check for deletion marker
            if (importSettings.enableDeletion && 
                categories.some(cat => cat.toLowerCase() === 'delete')) {
              quote.toDelete = true;
            } else {
              quote.categories = categories;
            }
          }
        }
        
        // Validate the quote
        validateQuote(quote);
        
        return quote;
      });
      
      setPreviewData(processedData);
    } catch (err) {
      setError(`Error processing data: ${err instanceof Error ? err.message : String(err)}`);
    }
  }, [parsedData, columnMapping, importSettings.enableDeletion]);

  // Validate a quote
  const validateQuote = (quote: ParsedQuote) => {
    if (!quote.validationErrors) {
      quote.validationErrors = [];
    }
    
    // If it's marked for deletion, no further validation needed
    if (quote.toDelete) {
      return true;
    }
    
    // Content is required
    if (!quote.content || quote.content.trim().length === 0) {
      quote.validationErrors.push("Quote content is required");
    } else if (quote.content.length > 1000) {
      quote.validationErrors.push("Quote content is too long (max 1000 characters)");
    }
    
    // Category validation can be added here
    
    return quote.validationErrors.length === 0;
  };

  // Start the import process
  const startImport = useCallback(async () => {
    if (!parsedData || !parsedData.data || !columnMapping.content) {
      throw new Error("No data to import or content column not mapped");
    }
    
    setIsImporting(true);
    setImportProgress(0);
    
    // Create a new abort controller for this import
    const controller = new AbortController();
    setAbortController(controller);
    
    try {
      // Signal object to check if import was aborted
      const signal = controller.signal;
      
      // Get existing categories for matching
      const existingCategories = categories || [];
      const categoryMap = new Map(
        existingCategories.map(cat => [cat.name.toLowerCase(), cat.id])
      );
      
      // Process the data into batches
      const totalRows = parsedData.data.length;
      const batchSize = importSettings.batchSize;
      const batches = Math.ceil(totalRows / batchSize);
      
      let successful = 0;
      let failed = 0;
      let deleted = 0;
      let processedRows = 0;
      const errors: ImportError[] = [];
      const categoryResults = new Map<string, CategoryResult>();
      const deletions: { content: string; id: string }[] = [];
      
      // Process each batch
      for (let batchIndex = 0; batchIndex < batches; batchIndex++) {
        if (signal.aborted) {
          throw new Error("Import aborted");
        }
        
        const start = batchIndex * batchSize;
        const end = Math.min(start + batchSize, totalRows);
        const batchData = parsedData.data.slice(start, end);
        
        // Process each row in the batch
        const batchQuotes: ParsedQuote[] = [];
        const batchRowIndices: number[] = [];
        const quotesToDelete: { content: string; row: number }[] = [];
        
        for (let i = 0; i < batchData.length; i++) {
          const rowIndex = start + i;
          const row = batchData[i];
          
          // Create quote object
          const quote: ParsedQuote = {
            content: row[columnMapping.content]?.trim() || "",
          };
          
          // Add author if mapped
          if (columnMapping.author && row[columnMapping.author]) {
            quote.author = row[columnMapping.author].trim();
          }
          
          // Add source if mapped
          if (columnMapping.source && row[columnMapping.source]) {
            quote.source = row[columnMapping.source].trim();
          }
          
          // Process categories if mapped
          if (columnMapping.categories && row[columnMapping.categories]) {
            const categoriesString = row[columnMapping.categories].trim();
            if (categoriesString) {
              const categories = categoriesString
                .split(columnMapping.categoryDelimiter || ",")
                .map(cat => cat.trim())
                .filter(cat => cat.length > 0);
              
              // Check for deletion marker
              if (importSettings.enableDeletion && 
                  categories.some(cat => cat.toLowerCase() === 'delete')) {
                quote.toDelete = true;
                quotesToDelete.push({ 
                  content: quote.content,
                  row: rowIndex
                });
                continue;
              } else {
                quote.categories = categories;
              }
            }
          }
          
          // Validate the quote
          validateQuote(quote);
          
          // If valid, add to batch quotes
          if (!quote.validationErrors || quote.validationErrors.length === 0) {
            batchQuotes.push(quote);
            batchRowIndices.push(rowIndex);
          } else {
            // Add to errors
            errors.push({
              row: rowIndex + 1, // 1-based indexing for users
              content: quote.content,
              error: quote.validationErrors.join("; ")
            });
            failed++;
          }
          
          processedRows++;
        }
        
        // Process quotes to delete
        if (quotesToDelete.length > 0 && importSettings.enableDeletion) {
          for (const { content, row } of quotesToDelete) {
            try {
              // Find quotes with matching content
              const { data: matchingQuotes, error: findError } = await supabase
                .from("quotes")
                .select("id, content")
                .ilike("content", content);
              
              if (findError) throw findError;
              
              if (matchingQuotes && matchingQuotes.length > 0) {
                for (const quote of matchingQuotes) {
                  // Delete quote categories first
                  const { error: catDeleteError } = await supabase
                    .from("quote_categories")
                    .delete()
                    .eq("quote_id", quote.id);
                  
                  if (catDeleteError) throw catDeleteError;
                  
                  // Then delete the quote
                  const { error: deleteError } = await supabase
                    .from("quotes")
                    .delete()
                    .eq("id", quote.id);
                  
                  if (deleteError) throw deleteError;
                  
                  deletions.push({
                    content: quote.content,
                    id: quote.id
                  });
                  
                  deleted++;
                }
                
                successful++;
              } else {
                // No matching quotes found
                errors.push({
                  row: row + 1,
                  content,
                  error: "No matching quote found to delete"
                });
                failed++;
              }
            } catch (err) {
              errors.push({
                row: row + 1,
                content,
                error: err instanceof Error ? err.message : String(err)
              });
              failed++;
            }
            
            processedRows++;
          }
        }
        
        if (batchQuotes.length > 0) {
          // Process batch quotes
          const result = await processBatchQuotes(
            batchQuotes, 
            batchRowIndices,
            categoryMap,
            categoryResults,
            importSettings.createMissingCategories,
            importSettings.skipDuplicateQuotes
          );
          
          successful += result.successful;
          failed += result.failed;
          
          // Add errors from batch processing
          if (result.errors && result.errors.length > 0) {
            errors.push(...result.errors);
          }
        }
        
        // Update progress
        setImportProgress((processedRows / totalRows) * 100);
      }
      
      // Prepare category results for display
      const categoryResultsArray: CategoryResult[] = Array.from(categoryResults.values())
        .sort((a, b) => b.count - a.count);
      
      // Set results
      const finalResults: ImportResults = {
        successful,
        failed,
        totalRows,
        processedRows,
        errors: errors.length > 0 ? errors : undefined,
        categories: categoryResultsArray.length > 0 ? categoryResultsArray : undefined
      };
      
      // Add deletion results if any
      if (deleted > 0) {
        finalResults.deleted = deleted;
        finalResults.deletions = deletions;
      }
      
      setImportResults(finalResults);
      setIsImporting(false);
      
      // Return results
      return finalResults;
    } catch (err) {
      setIsImporting(false);
      throw err;
    } finally {
      // Clean up abort controller
      setAbortController(null);
    }
  }, [
    parsedData, 
    columnMapping, 
    importSettings, 
    categories
  ]);

  // Process a batch of quotes
  const processBatchQuotes = async (
    quotes: ParsedQuote[],
    rowIndices: number[],
    categoryMap: Map<string, string>,
    categoryResults: Map<string, CategoryResult>,
    createMissingCategories: boolean,
    skipDuplicates: boolean
  ) => {
    let successful = 0;
    let failed = 0;
    const errors: ImportError[] = [];
    
    // Insert quotes and manage categories
    for (let i = 0; i < quotes.length; i++) {
      const quote = quotes[i];
      const rowIndex = rowIndices[i];
      
      try {
        // Check for duplicate quotes if enabled
        if (skipDuplicates) {
          const { data: existingQuotes, error: checkError } = await supabase
            .from("quotes")
            .select("id")
            .eq("content", quote.content)
            .limit(1);
          
          if (checkError) throw checkError;
          
          if (existingQuotes && existingQuotes.length > 0) {
            errors.push({
              row: rowIndex + 1, // 1-based indexing for users
              content: quote.content,
              error: "Duplicate quote - skipped"
            });
            failed++;
            continue;
          }
        }
        
        // Insert the quote
        const { data: newQuote, error: insertError } = await supabase
          .from("quotes")
          .insert({
            content: quote.content,
            author: quote.author || null,
            source: quote.source || null
          })
          .select()
          .single();
        
        if (insertError) throw insertError;
        
        // Process categories if present
        if (quote.categories && quote.categories.length > 0) {
          const categoryIds: string[] = [];
          
          for (const categoryName of quote.categories) {
            const normalizedName = categoryName.toLowerCase();
            
            // Check if category exists
            let categoryId = categoryMap.get(normalizedName);
            
            if (!categoryId && createMissingCategories) {
              // Create the category
              const { data: newCategory, error: categoryError } = await supabase
                .from("categories")
                .insert({ name: categoryName })
                .select()
                .single();
              
              if (categoryError) throw categoryError;
              
              categoryId = newCategory.id;
              categoryMap.set(normalizedName, categoryId);
            }
            
            if (categoryId) {
              categoryIds.push(categoryId);
              
              // Update category results
              if (categoryResults.has(categoryId)) {
                const catResult = categoryResults.get(categoryId)!;
                catResult.count++;
                categoryResults.set(categoryId, catResult);
              } else {
                categoryResults.set(categoryId, {
                  id: categoryId,
                  name: categoryName,
                  count: 1,
                  isNew: !categoryMap.has(normalizedName)
                });
              }
            }
          }
          
          // Add category associations
          for (const categoryId of categoryIds) {
            const { error: associationError } = await supabase
              .from("quote_categories")
              .insert({
                quote_id: newQuote.id,
                category_id: categoryId
              });
            
            if (associationError) throw associationError;
          }
        }
        
        successful++;
      } catch (err) {
        errors.push({
          row: rowIndex + 1, // 1-based indexing for users
          content: quote.content,
          error: err instanceof Error ? err.message : String(err)
        });
        failed++;
      }
    }
    
    return {
      successful,
      failed,
      errors
    };
  };

  // Cancel the import process
  const cancelImport = useCallback(() => {
    if (abortController) {
      abortController.abort();
      setIsImporting(false);
      toast({
        title: "Import cancelled",
        description: "The import process has been cancelled."
      });
    }
  }, [abortController, toast]);

  // Validate the file
  const validateFile = useCallback((file: File): boolean => {
    // Check file type
    const validExtensions = ['.csv', '.txt', '.tsv'];
    const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
    
    if (!validExtensions.includes(fileExtension)) {
      setError("Invalid file type. Please upload a CSV, TSV, or TXT file.");
      return false;
    }
    
    // Check file size (10MB max)
    const maxSizeInBytes = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSizeInBytes) {
      setError("File is too large. Maximum size is 10MB.");
      return false;
    }
    
    return true;
  }, []);

  // Update column mapping
  const updateColumnMapping = useCallback((mapping: ColumnMapping) => {
    setColumnMapping(mapping);
  }, []);

  // Update import settings
  const updateImportSettings = useCallback((settings: ImportSettings) => {
    setImportSettings(settings);
  }, []);

  // Download a template CSV file
  const downloadTemplate = useCallback(() => {
    const templateData = [
      ["Content", "Author", "Source", "Categories"],
      ["Life is what happens when you're busy making other plans.", "John Lennon", "Interview", "Life,Wisdom"],
      ["The journey of a thousand miles begins with one step.", "Lao Tzu", "Tao Te Ching", "Wisdom,Journey"],
      ["Be the change that you wish to see in the world.", "Mahatma Gandhi", "", "Inspiration,Change"],
      ["This is a quote to be deleted", "Delete Example", "", "delete"]
    ];
    
    const csv = templateData.map(row => row.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", "quotes-template.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, []);

  // Utility function to read file as text with specified encoding
  const readFileAsText = (file: File, encoding: string): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (event) => {
        if (event.target?.result) {
          resolve(event.target.result as string);
        } else {
          reject(new Error("Failed to read file"));
        }
      };
      
      reader.onerror = () => {
        reject(new Error("Error reading file"));
      };
      
      // Set encoding
      if (encoding === "UTF-8") {
        reader.readAsText(file, "UTF-8");
      } else if (encoding === "ISO-8859-1") {
        reader.readAsText(file, "ISO-8859-1");
      } else if (encoding === "UTF-16") {
        reader.readAsText(file, "UTF-16");
      } else {
        reader.readAsText(file); // Default encoding
      }
    });
  };

  // This function is now moved outside the export statement and made a regular function inside the hook
  const processQuoteForImport = async (
    quote: any, 
    columnMapping: any, 
    settings: any,
    categories: any[]
  ) => {
    const { content, author, source, categories: categoryNames, id } = quote;
    
    // Check if ID is provided and if we should update existing quotes
    if (id && settings.updateExistingQuotes) {
      // Check if quote with this ID exists
      const { data } = await supabase
        .from("quotes")
        .select("id")
        .eq("id", id)
        .maybeSingle();
        
      if (data) {
        // This is an existing quote that should be updated
        quote._isUpdate = true;
      }
    }
    
    // Convert category names to IDs
    if (categoryNames && categoryNames.length > 0) {
      const categoryIds = [];
      
      for (const name of categoryNames) {
        // Try to find existing category
        let category = categories.find(c => 
          c.name.toLowerCase() === name.toLowerCase());
        
        // Create category if it doesn't exist and settings allow it
        if (!category && settings.createMissingCategories) {
          try {
            const { data, error } = await supabase
              .from("categories")
              .insert({ name })
              .select("id, name")
              .single();
              
            if (!error) {
              category = data;
              categories.push(category); // Add to local cache
            }
          } catch (err) {
            console.error("Error creating category:", err);
          }
        }
        
        if (category) {
          categoryIds.push(category.id);
        }
      }
      
      quote.categoryIds = categoryIds;
    }
    
    return quote;
  };

  return {
    file,
    parsedData,
    columnMapping,
    importSettings,
    previewData,
    importProgress,
    importResults,
    isImporting,
    isLoading,
    error,
    setFile,
    updateColumnMapping,
    updateImportSettings,
    startImport,
    cancelImport,
    validateFile,
    downloadTemplate,
    processQuoteForImport
  };
};
